/**
 * Product Configuration Data
 * 
 * This module contains the actual configuration data for perils, currencies,
 * and agricultural activities used in product configuration.
 */

import type { ProductConfig as ProductConfigData, PerilConfig, CurrencyConfig, ActivityConfig, CoverageItemConfig } from './types';
import type { ProductConfig } from '../../types/product';

/**
 * Available weather perils for insurance coverage
 */
export const perils: PerilConfig[] = [
  {
    id: 'Deficit',
    name: 'Drought / Deficit',
    description: 'Insufficient rainfall or water availability',
    common: true,
    color: '#D97706',
    icon: 'sun',
  },
  {
    id: 'Excess',
    name: 'Excess Rain / Flooding',
    description: 'Excessive rainfall leading to flooding',
    common: true,
    color: '#2563EB',
    icon: 'cloud-rain',
  },
  {
    id: 'high-temperature',
    name: 'High temperature',
    description: 'Extreme heat that damages crops',
    common: false,
    color: '#DC2626',
    icon: 'thermometer'
  },
  {
    id: 'low-temperature',
    name: 'Low temperature',
    description: 'Frost or extreme cold that damages crops',
    common: false,
    color: '#0EA5E9',
    icon: 'snowflake'
  },
  {
    id: 'hail',
    name: 'Hail',
    description: 'Ice pellets that physically damage crops',
    common: true,
    color: '#6B7280',
    icon: 'cloud-hail'
  },
  {
    id: 'strong-wind',
    name: 'Strong wind',
    description: 'High winds that damage or destroy crops',
    common: false,
    color: '#059669',
    icon: 'wind'
  },
  {
    id: 'PoorRangelands',
    name: 'Poor Rangelands',
    description: 'Degraded rangeland quality affecting livestock',
    common: false,
    color: '#8B4513',
    icon: 'mountain',
  },
  {
    id: 'Germination',
    name: 'Dry Spell / Germination',
    description: 'Period of below-average rainfall during germination',
    common: false,
    color: '#F59E0B',
    icon: 'cloud-sun',
  }
];

/**
 * Available coverage items (items insured) for insurance coverage
 */
export const coverageItems: CoverageItemConfig[] = [
  {
    id: 'loan_credit',
    name: 'Loan/Credit',
    description: 'Coverage for loans and credit facilities',
    icon: '💰',
    common: true
  },
  {
    id: 'input_agreement',
    name: 'Input Agreement',
    description: 'Coverage for agricultural inputs and supplies',
    icon: '🌱',
    common: true
  },
  {
    id: 'farm_asset',
    name: 'Farm Asset',
    description: 'Coverage for farm equipment and infrastructure',
    icon: '🚜',
    common: true
  },
  {
    id: 'activity_product_value',
    name: 'Activity/Product Value',
    description: 'Coverage for the value of agricultural activities and products',
    icon: '📊',
    common: false
  },
  {
    id: 'household_sustenance',
    name: 'Household Sustenance',
    description: 'Coverage for household food security and sustenance',
    icon: '🏠',
    common: false
  }
];

/**
 * Supported currencies
 */
export const currencies: CurrencyConfig[] = [
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    decimals: 2,
    common: true
  },
  {
    code: 'ZWG',
    name: 'Zimbabwe Gold',
    symbol: 'ZWG',
    decimals: 2,
    common: true
  },
  {
    code: 'ZWL',
    name: 'Zimbabwe Dollar',
    symbol: 'Z$',
    decimals: 2,
    common: false
  }
];

/**
 * Available agricultural activities and products
 */
export const activities: ActivityConfig[] = [
  // Crop Production
  {
    id: 'maize',
    name: 'Maize',
    category: 'crops',
    description: 'Corn production',
    common: true,
    aliases: ['corn']
  },
  {
    id: 'sorghum',
    name: 'Sorghum',
    category: 'crops',
    description: 'Sorghum grain production',
    common: true
  },
  {
    id: 'millet',
    name: 'Millet',
    category: 'crops',
    description: 'Millet grain production',
    common: false
  },
  {
    id: 'groundnuts',
    name: 'Groundnuts',
    category: 'crops',
    description: 'Peanut production',
    common: true,
    aliases: ['peanuts']
  },
  {
    id: 'sunflower',
    name: 'Sunflower',
    category: 'crops',
    description: 'Sunflower seed production',
    common: false
  },
  {
    id: 'soybeans',
    name: 'Soybeans',
    category: 'crops',
    description: 'Soybean production',
    common: true,
    aliases: ['soya beans']
  },
  {
    id: 'cotton',
    name: 'Cotton',
    category: 'crops',
    description: 'Cotton fiber production',
    common: true
  },
  {
    id: 'tobacco',
    name: 'Tobacco',
    category: 'crops',
    description: 'Tobacco leaf production',
    common: true
  },
  {
    id: 'horticulture',
    name: 'Horticulture (vegetables, fruits)',
    category: 'crops',
    description: 'Vegetable and fruit production',
    common: true,
    aliases: ['vegetables', 'fruits', 'garden crops']
  },
  
  // Livestock Production
  {
    id: 'dairy-production',
    name: 'Dairy production',
    category: 'livestock',
    description: 'Milk and dairy product production',
    common: true,
    aliases: ['dairy farming', 'milk production']
  },
  {
    id: 'poultry',
    name: 'Poultry',
    category: 'livestock',
    description: 'Chicken and egg production',
    common: true,
    aliases: ['chicken farming', 'egg production']
  },
  {
    id: 'piggery',
    name: 'Piggery',
    category: 'livestock',
    description: 'Pig farming and pork production',
    common: false,
    aliases: ['pig farming', 'swine production']
  },
  {
    id: 'cattle-goat-sheep-rearing',
    name: 'Cattle/Goat/Sheep rearing',
    category: 'livestock',
    description: 'Ruminant livestock production',
    common: true,
    aliases: ['cattle farming', 'goat farming', 'sheep farming', 'livestock farming']
  },
  
  // Other Activities
  {
    id: 'apiculture',
    name: 'Apiculture',
    category: 'other',
    description: 'Beekeeping and honey production',
    common: false,
    aliases: ['beekeeping', 'honey production']
  },
  {
    id: 'fish-farming-aquaculture',
    name: 'Fish farming/Aquaculture',
    category: 'other',
    description: 'Fish and aquatic animal production',
    common: false,
    aliases: ['fish farming', 'aquaculture', 'fisheries']
  },
  {
    id: 'agroforestry',
    name: 'Agroforestry',
    category: 'other',
    description: 'Combined agriculture and forestry practices',
    common: false,
    aliases: ['forest farming', 'tree farming']
  },
  {
    id: 'other',
    name: 'Other',
    category: 'other',
    description: 'Other agricultural activities not listed above',
    common: false
  }
];

/**
 * Main product configuration object
 */
export const productConfig: ProductConfigData = {
  perils,
  currencies,
  activities,
  coverageItems
};

/**
 * Default product configuration for new products
 */
export const defaultProductConfig: ProductConfig = {
  _version: '1.5.0',
  coverageConfig: {
    activitiesOrProducts: ['maize', 'sorghum', 'groundnuts'],
    perils: ['Deficit', 'Excess'],
    areaOptions: ['Country-wide'],
    coveragePeriodLimits: {
      type: 'duration_days',
      minDurationDays: 90,
      maxDurationDays: 365,
    },
    sumInsuredLimits: {
      min: 500,
      max: 100000,
    },
    coverageLevelGlobalLimits: {},
    currency: 'USD' // Primary currency field for the entire product
  },
  loadingConfig: {
    taxRate: 0.15, // 15% tax rate
    subsidyRate: 0.0, // No subsidy by default
    loadingFactors: [], // Empty - to be configured per administrative unit
    perilWeighting: {
      enforcement: 'default',
      weights: {}
    },
    coverageLevelLimits: {
      enforcement: 'default',
      limits: {}
    },
  },
  stakeholdersAnrpDistribution: {
    administration: {
      insurer: {
        organizationId: '00000000-0000-0000-0000-000000000001',
        name: 'Select Insurance Company',
        percentage: 0.8, // 80%
        role: 'Insurer',
      },
      myubuntu: {
        organizationId: '00000000-0000-0000-0000-000000000002',
        name: 'MyUbuntu Ltd',
        percentage: 0.15, // 15%
        role: 'MyUbuntu',
      },
    },
    underwriters: [], // To be configured
    brokers: [],
    calculatingAgents: [],
    otherOrganisations: [],
  },
  pricingFactors: {
    minimumPremium: 75,
    administrativeFee: 35,
    underwritingLoading: 0.03, // 3%
    agentCommissionRate: 0.12, // 12%
    brokerCommissionRate: 0.08, // 8%
    subsidyRate: 0, // No subsidy by default
  },
};

// =============================================================================
// PERIL API FUNCTIONS
// =============================================================================

import { PERIL_IDS } from '../../types/perils';

/**
 * Get supported peril IDs for quotation API.
 * With PascalCase IDs, all registered R-Server perils are directly supported.
 */
export function getSupportedQuotationPerils(): string[] {
  return [...PERIL_IDS];
}

/**
 * Validate if a product's perils are supported by the quotation API
 */
export function validateQuotationSupport(productPerils: string[]): {
  supported: string[];
  unsupported: string[];
  isSupported: boolean;
} {
  const supportedPerils = getSupportedQuotationPerils();
  const perilsArray = productPerils || [];
  
  const supported = perilsArray.filter(peril => supportedPerils.includes(peril));
  const unsupported = perilsArray.filter(peril => !supportedPerils.includes(peril));
  
  return {
    supported,
    unsupported,
    isSupported: supported.length > 0 && unsupported.length === 0
  };
}

/**
 * Transform our peril configuration to R-Server API format.
 * Since internal IDs now match R-Server PascalCase names, no mapping is needed.
 */
export function transformPerilsForApi(
  productPerils: string[], 
  perilWeights: Record<string, { min: number; max: number }>,
  protectionLevels: Record<string, { strike: number; exhaust: number }>
) {
  return productPerils
    .map(perilId => {
      const weight = perilWeights[perilId];
      const levels = protectionLevels[perilId];
      
      return {
        peril: perilId,
        weight: weight?.min || weight?.max || 50,
        strike: levels?.strike || 10,
        exhaust: levels?.exhaust || 20
      };
    });
}
